import { observable, action, autorun, computed } from 'mobx';
import axios from 'axios';

import { ghPreselectedNumberType, ghTollFreeIntent } from '~/common/globals.js';
import { isAreaCodePremium } from '~/common/utils/premiumAreaCodes.js';
import { extractAreaCode } from '~/common/utils/numberOperations.js';
import preselectedNumberObject from '~/common/utils/preselectedNumberObject.js';
import verifyPreselectedNumber from '~/common/utils/verifyPreselectedNumber.js';
import { isBoolean, isArray } from '~/common/utils/typeCheckers';

// import other stores
import { numberLockSessionStore } from '~/common/stores/domain/numberLockSession.js';
import { lockNumberStore } from '~/common/stores/domain/lockNumber.js';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite';

class TollFreeNumbersClass {
    @observable tollFreeNumbers = [];
    @observable true800Numbers = [];
    @observable tollFreeNumbersError = false;
    @observable true800NumbersError = false;
    @observable preselectedTollFreeIntent = false;

    bootstrapStore() {
        autorun((reaction) => {
            // if there is a number lock session or the number lock session changes
            if (decodeURI(ghPreselectedNumberType()).toLowerCase() === 'toll free') {
                // set preselected number if available
                this.setPreselectedNumber();
                reaction.dispose();
            }
        });

        autorun((reaction) => {
            if (ghTollFreeIntent() && ghTollFreeIntent().toLowerCase() === 'true') {
                this.setPreselectedTollFreeIntent(true);
                reaction.dispose();
            }
        });
    }

    @action setPreselectedNumber() {
        if (verifyPreselectedNumber('Toll Free')) {
            // set flag that the number was preselected on the marketing site
            lockNumberStore.setIsNumberPreselected(true);
            // set selected number with number user preselected
            lockNumberStore.setSelectedNumber(preselectedNumberObject());
            // set locked number type
            lockNumberStore.setSelectedNumberType('Toll Free');
            // lock number to session
            lockNumberStore.lockNumberToSession(lockNumberStore.selectedNumberType);
        }
    }

    @action getTollFreeNumbers(count = 11, callback) {
        this.clearTollFreeNumbers();
        this.setTollFreeNumbersError(false);
        axios.get(`/api/NumberCatalog/ghnumber?city=TF&areaCode=8**&numberLockSessionId=${numberLockSessionStore.NLS}&count=${count}${offerSuiteStore.selectedOfferId ? `&offerId=${offerSuiteStore.selectedOfferId}` : ''}`)
            .then((response) => {
                // if response object
                if (typeof response !== 'object' && typeof response.data !== 'object') throw new Error('invalid response format');
                // set numbers
                this.setTollFreeNumbers(response.data);
                if (callback) callback();
            }).catch((err) => {
                // set error flag to true
                this.setTollFreeNumbersError(true);
                return err;
            });
    }

    @action get800Numbers(count = 1, callback) {
        axios.get(`/api/NumberCatalog/ghnumber?city=TF&areaCode=800&numberLockSessionId=${numberLockSessionStore.NLS}&count=${count}${offerSuiteStore.selectedOfferId ? `&offerId=${offerSuiteStore.selectedOfferId}` : ''}`)
            .then((response) => {
                // if response object
                if (typeof response !== 'object' && typeof response.data !== 'object') throw new Error('invalid response format');
                // set numbers
                this.set800Numbers(response.data);
                if (callback) callback();
            }).catch((err) => {
                // set error flag to true
                this.set800NumbersError(true);
                return err;
            });
    }

    @action setPreselectedTollFreeIntent(bool) {
        this.preselectedTollFreeIntent = isBoolean(bool, this.preselectedTollFreeIntent);
    }

    @action set800NumbersError(bool) {
        this.true800NumbersError = isBoolean(bool, this.true800NumbersError);
    }

    @action set800Numbers(data) {
        this.true800Numbers = isArray(data, this.true800Numbers);
    }

    // reset toll free numbers array
    @action clearTollFreeNumbers() {
        this.tollFreeNumbers = [];
    }

    @action setTollFreeNumbers(data) {
        this.tollFreeNumbers = isArray(data, this.tollFreeNumbers);
    }

    @action setTollFreeNumbersError(bool) {
        this.tollFreeNumbersError = isBoolean(bool, this.tollFreeNumbersError);
    }

    @computed get fetchedTollFreeNumbers() {
        return this.tollFreeNumbers && this.tollFreeNumbers.length > 0;
    }

    @computed get fetched800Numbers() {
        return this.true800Numbers && this.true800Numbers.length > 0;
    }

    // finds the first toll free number that isn't premium (i.e. not an 800 area code)
    @computed get trialValidNumber() {
        if (!Array.isArray(this.tollFreeNumbers.slice())) return null;

        return this.tollFreeNumbers.find((num) => {
            const areaCode = extractAreaCode(num);
            if (!areaCode) return false;
            return !isAreaCodePremium(areaCode);
        }) || null;
    }
}

const tollFreeNumbersStore = new TollFreeNumbersClass();
export { tollFreeNumbersStore, TollFreeNumbersClass };
